import {
  HARNESS_IDS,
  type FlowSchedule,
  type HarnessId,
  type UpdateFlowScheduleRequest,
} from "@cohesive/api-client";

/** What a schedule's runs are stamped with, as the automation forms hold it. */
export interface AutomationHarnessSelection {
  harness: HarnessId;
  /** Undefined means each run resolves the harness default at fire time. */
  model?: string;
  /** Undefined means each run takes the owner's default effort for the model. */
  effort?: string;
}

export const DEFAULT_AUTOMATION_HARNESS: AutomationHarnessSelection = {
  harness: HARNESS_IDS.COHESIVE,
};

/** A schedule that predates the stamp reads as cohesive, which is what it ran on. */
export function automationHarnessOf(
  schedule: FlowSchedule,
): AutomationHarnessSelection {
  return {
    harness: schedule.harness ?? DEFAULT_AUTOMATION_HARNESS.harness,
    model: schedule.model || undefined,
    effort: schedule.reasoning_effort || undefined,
  };
}

export function automationHarnessDirty(
  saved: AutomationHarnessSelection,
  next: AutomationHarnessSelection,
): boolean {
  return (
    saved.harness !== next.harness ||
    saved.model !== next.model ||
    saved.effort !== next.effort
  );
}

/**
 * The harness half of a PATCH body.
 *
 * The model always rides a harness change: the server validates it against whichever harness
 * the row lands on, and the vocabularies don't cross, so leaving the old harness's model
 * behind is what a 400 looks like. `null` clears the pin back to the harness default —
 * omitting the key would keep it, which is the one thing a chat message cannot express. The
 * effort is sent only when it changed: the server applies the same keep-or-clear rule the form
 * does on a harness or model change.
 */
export function automationHarnessPatch(
  saved: AutomationHarnessSelection,
  next: AutomationHarnessSelection,
): Pick<UpdateFlowScheduleRequest, "harness" | "model" | "reasoning_effort"> {
  if (!automationHarnessDirty(saved, next)) return {};
  return {
    ...(next.harness !== saved.harness ? { harness: next.harness } : {}),
    model: next.model ?? null,
    ...(next.effort !== saved.effort
      ? { reasoning_effort: next.effort ?? null }
      : {}),
  };
}
